import { FC, useState } from "react";
import { Avatar, Box, Typography, Tooltip } from "@mui/material";
import SpeedIcon from "@mui/icons-material/Speed";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { PostOrder } from "../../../../../types";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { getTime } from "../../../../../utils/getTime";
import { StatusBar } from "../../../../../ui/atoms/StatusBar";
import { CancelPostOrderModal } from "../../modals/CancelPostOrderModal";
import { canCancelPostOrder } from "../../../../../utils/canCancelPostOrder";
import { EditPostOrderModal } from "../../modals/EditPostOrderModal";
import { canUpdatePostOrder } from "../../../../../utils/canUpdatePostOrder";
import { PostOrderMoreButton } from "../PostOrderMoreButton";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useTranslation } from "react-i18next";

interface PostItemProps {
  postOrder: PostOrder;
  gridTemplateColumns?: string;
  refetch: () => void;
}

export const PostItem: FC<PostItemProps> = ({
  postOrder,
  gridTemplateColumns = "1fr",
  refetch,
}) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<"cancel" | "edit" | null>(null);

  const handleCloseModal = () => {
    setModal(null);
  };

  return (
    <>
      <Box
        sx={{
          height: "40px",
          borderBottom: "1px solid #e0e0e0",
          display: { xs: "none", sm: "none", md: "grid" },
          gridTemplateColumns: gridTemplateColumns,
          px: "24px",
          paddingBottom: "8px",
          paddingTop: "8px",
          background: "#fff",
          "&:hover": {
            background: "#f5f5f5",
          },
          "&:active": {
            background: "#f5f5f5",
          },
          "&:last-child": {
            borderBottom: "none",
            borderRadius: "0 0 16px 16px",
            mb: "24px",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", maxWidth: "250px" }}>
          <Avatar
            alt={postOrder.channel.title}
            src={postOrder.channel.picture}
            sx={{ mr: "12px" }}
          />
          <Box
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Box
              sx={{
                display: "flex",
              }}
            >
              <Typography
                sx={{
                  fontWeightа: "bold",
                  fontSize: "1rem",
                  color: "#666666",
                  cursor: "pointer",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                onClick={() => window.open(postOrder.post_link)}
              >
                {postOrder?.channel?.title}
              </Typography>
              <Typography
                sx={{
                  fontWeightа: "bold",
                  fontSize: "1rem",
                  color: "#666666",
                  cursor: "pointer",
                  ml: "4px",
                }}
                onClick={() => window.open(postOrder.post_link)}
              >
                #{postOrder.post_id}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.875rem",
                color: "rgb(108, 115, 127)",
              }}
            >
              {new Date(postOrder.created_at).toDateString()}{" "}
              {getTime(new Date(postOrder.created_at))}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "8px",
            minWidth: "100px",
            maxWidth: "120px",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {postOrder.views_mode.value === "ORGANIC" ? (
            <PersonIcon sx={{ color: "#666666", mr: "8px" }} />
          ) : (
            <SpeedIcon sx={{ color: "#666666", mr: "8px" }} />
          )}
          <Typography
            sx={{
              width: "80%",
              overflow: "hidden",

              color: "#666666",
            }}
          >
            {postOrder.speed || "-"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "8px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <Tooltip
            title={
              postOrder.include_other_sources
                ? t("includedOtherSources")
                : t("notIncludedOtherSources")
            }
            placement="bottom"
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {postOrder.include_other_sources ? (
                <VisibilityIcon sx={{ color: "#666666", mr: "8px" }} />
              ) : (
                <RemoveRedEyeOutlinedIcon sx={{ color: "#666666", mr: "8px" }} />
              )}
              <Typography
                sx={{
                  color: "#666666",
                }}
              >
                {postOrder.include_other_sources ? (
                    (postOrder.real_views?.title || "-") +
                    " / " +
                    (postOrder?.awaiting_views?.title || "-")
                  ) : (
                    (postOrder.completed_views?.title || "-") +
                    " / " +
                    (postOrder?.views?.title || "-")
                  )}
              </Typography>
            </div>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "8px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100px",
          }}
        >
          <Typography
            sx={{
              color: "#666666",
              maxWidth: "100px",
            }}
          >
            $ {postOrder.price || "-"}
          </Typography>
        </Box>
        <StatusBar status={postOrder.status.value} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            color: "#666666",
            marginLeft: "10px",
          }}
        >
          <PostOrderMoreButton
            canUpdate={canUpdatePostOrder(postOrder.status.value)}
            canCancel={canCancelPostOrder(postOrder.status.value)}
            setModal={(arg: "edit" | "cancel") => setModal(arg)}
          />
        </Box>
      </Box>
      <Box
        sx={{
          height: "120px",
          borderBottom: "1px solid #e0e0e0",
          display: { xs: "flex", sm: "flex", md: "none" },
          flexDirection: "column",
          px: "12px",
          paddingBottom: "8px",
          paddingTop: "8px",
          background: "#fff",
          "&:hover": {
            background: "#f5f5f5",
          },
          "&:active": {
            background: "#f5f5f5",
          },
          "&:last-child": {
            borderBottom: "none",
            borderRadius: "0 0 16px 16px",
            mb: "24px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flex: "1",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", maxWidth: "250px" }}
          >
            <Avatar
              alt={postOrder.channel.title}
              src={postOrder.channel.picture}
              sx={{ mr: "12px" }}
            />
            <Box
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Typography
                  sx={{
                    fontWeightа: "bold",
                    fontSize: "1rem",
                    color: "#666666",
                    cursor: "pointer",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  onClick={() => window.open(postOrder.post_link)}
                >
                  {postOrder?.channel?.title}
                </Typography>
                <Typography
                  sx={{
                    fontWeightа: "bold",
                    fontSize: "1rem",
                    color: "#666666",
                    cursor: "pointer",
                    ml: "4px",
                  }}
                  onClick={() => window.open(postOrder.post_link)}
                >
                  #{postOrder.post_id}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: "rgb(108, 115, 127)",
                }}
              >
                {new Date(postOrder.created_at).toDateString()}{" "}
                {getTime(new Date(postOrder.created_at))}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              color: "#666666",
              marginLeft: "10px",
            }}
          >
            <PostOrderMoreButton
              canUpdate={canUpdatePostOrder(postOrder.status.value)}
              canCancel={canCancelPostOrder(postOrder.status.value)}
              setModal={(arg: "edit" | "cancel") => setModal(arg)}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center",
            justifyContent: "space-between",
            mt: "12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: "8px",

              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {postOrder.views_mode.value === "ORGANIC" ? (
              <PersonIcon sx={{ color: "#666666", mr: "8px" }} />
            ) : (
              <SpeedIcon sx={{ color: "#666666", mr: "8px" }} />
            )}
            <Typography
              sx={{
                width: "80%",
                overflow: "hidden",

                color: "#666666",
              }}
            >
              {postOrder.speed || "-"}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: "8px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              justifyContent: "end",
            }}
          >
            <Tooltip
              title={
                postOrder.include_other_sources
                  ? t("includedOtherSources")
                  : t("notIncludedOtherSources")
              }
              placement="bottom"
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {postOrder.include_other_sources ? (
                  <VisibilityIcon sx={{ color: "#666666", mr: "8px" }} />
                ) : (
                  <RemoveRedEyeOutlinedIcon sx={{ color: "#666666", mr: "8px" }} />
                )}
                <Typography
                  sx={{
                    color: "#666666",
                  }}
                >
                  {postOrder.include_other_sources ? (
                    (postOrder.real_views?.title || "-") +
                    " / " +
                    (postOrder?.awaiting_views?.title || "-")
                  ) : (
                    (postOrder.completed_views?.title || "-") +
                    " / " +
                    (postOrder?.views?.title || "-")
                  )}
                </Typography>
              </div>
            </Tooltip>
          </Box>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center",
            justifyContent: "space-between",
            mt: "12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: "8px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <AttachMoneyIcon sx={{ color: "#666666" }} />
            <Typography
              sx={{
                color: "#666666",
              }}
            >
              {postOrder.price || "-"}
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", justifyContent: "end", minWidth: "100px" }}
          >
            {" "}
            <StatusBar status={postOrder.status.value} />
          </Box>
        </Box>
      </Box>
      <CancelPostOrderModal
        open={modal === "cancel"}
        handleClose={handleCloseModal}
        id={postOrder.id}
        refetch={refetch}
      />
      <EditPostOrderModal
        open={modal === "edit"}
        handleClose={handleCloseModal}
        refetch={refetch}
        postOrder={postOrder}
      />
    </>
  );
};
